import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { socketAdmin } from '../../services/socketAuction'
import { LOT_STATUS } from '../../utils/constants'
import { colors } from '../../pages/admin/componentsV2/colors'

const STATUS_LABEL = {
  [LOT_STATUS.PENDIENTE]: 'Pendiente',
  [LOT_STATUS.ACTUAL]: 'Actual',
  [LOT_STATUS.SIN_POSTOR]: 'Sin postor',
  [LOT_STATUS.VENDIDO]: 'Vendido',
  [LOT_STATUS.RETIRADO]: 'Retirado',
}

const STATUS_COLOR = {
  [LOT_STATUS.PENDIENTE]: { bg: colors.surfaceAlt, fg: colors.muted },
  [LOT_STATUS.ACTUAL]: { bg: colors.navy, fg: '#fff' },
  [LOT_STATUS.SIN_POSTOR]: { bg: colors.amber, fg: '#fff' },
  [LOT_STATUS.VENDIDO]: { bg: colors.green, fg: '#fff' },
  [LOT_STATUS.RETIRADO]: { bg: colors.red, fg: '#fff' },
}

function reorder(list, sourceIndex, destinationIndex) {
  const result = Array.from(list)
  const [moved] = result.splice(sourceIndex, 1)
  result.splice(destinationIndex, 0, moved)
  return result
}

const scrollbarSx = {
  '&::-webkit-scrollbar': { width: 8 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: colors.border,
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: colors.muted,
  },
}

export default function LotsOrderDialog({ auction, open, onClose }) {
  const [lots, setLots] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setLots(auction?.lots || [])
      setSaving(false)
      setError('')
    }
    // Solo se sincroniza al abrir: mientras el dialogo esta abierto, el orden
    // local es el que el usuario esta armando y no debe pisarse con lo que
    // llegue por socket hasta que confirme.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleDragEnd = (result) => {
    if (!result.destination) return
    setLots((current) => reorder(current, result.source.index, result.destination.index))
  }

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  const handleAccept = () => {
    setSaving(true)
    setError('')
    socketAdmin.emit(
      'reorderAllLots',
      { auctionId: auction.id, lotIds: lots.map((lot) => lot.id) },
      (response) => {
        setSaving(false)
        if (response?.ok === false) {
          setError(response.message || 'No se pudo actualizar el orden de los lotes.')
          return
        }
        onClose()
      }
    )
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 64px rgba(15,38,71,.28)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          alignItems: 'center',
          background: `linear-gradient(135deg, ${colors.navy}, ${colors.blue})`,
          display: 'flex',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.75,
        }}
      >
        <Box>
          <Typography sx={{ color: '#fff', fontSize: 17, fontWeight: 800, lineHeight: 1.2 }}>
            Ordenar lotes
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,.75)', fontSize: 11.5, fontWeight: 600, mt: 0.25 }}>
            {lots.length} {lots.length === 1 ? 'lote' : 'lotes'}
          </Typography>
        </Box>
        <IconButton disabled={saving} onClick={handleClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: colors.surfaceAlt, px: 2.5, py: '20px !important' }}>
        <Typography sx={{ color: colors.muted, fontSize: 12.5, lineHeight: 1.5, mb: 1.75 }}>
          Arrastra los lotes desde{' '}
          <DragIndicatorIcon sx={{ color: colors.muted, fontSize: 14, verticalAlign: 'text-bottom' }} />{' '}
          para cambiar su orden. Al presionar <strong>Aceptar</strong> se actualizará para todos los
          usuarios y administradores conectados.
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ borderRadius: 1.5, fontSize: 13, mb: 1.75 }}>
            {error}
          </Alert>
        ) : null}

        <Box
          sx={{
            ...scrollbarSx,
            bgcolor: colors.background,
            border: `1px solid ${colors.border}`,
            borderRadius: 2.5,
            maxHeight: '55vh',
            overflowY: 'auto',
            p: 1,
          }}
        >
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="lots-order-droppable">
              {(droppableProvided) => (
                <Box
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
                >
                  {lots.map((lot, index) => {
                    const { bg, fg } = STATUS_COLOR[lot.status] || STATUS_COLOR[LOT_STATUS.PENDIENTE]
                    return (
                      <Draggable draggableId={String(lot.id)} index={index} key={lot.id}>
                        {(draggableProvided, snapshot) => (
                          <Box
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            sx={{
                              alignItems: 'center',
                              bgcolor: colors.surface,
                              border: `1px solid ${snapshot.isDragging ? colors.blue : colors.border}`,
                              borderRadius: 2,
                              boxShadow: snapshot.isDragging
                                ? '0 12px 28px -8px rgba(11,101,165,.45)'
                                : '0 1px 2px rgba(16,38,64,.05)',
                              display: 'flex',
                              gap: 1,
                              pl: 0.5,
                              pr: 1.25,
                              py: 0.85,
                              transform: snapshot.isDragging ? 'scale(1.015)' : 'none',
                              transition: snapshot.isDragging
                                ? undefined
                                : 'box-shadow .15s ease, border-color .15s ease, background-color .15s ease',
                              '&:hover': {
                                borderColor: colors.blue,
                                bgcolor: colors.paleBlue,
                              },
                            }}
                          >
                            <Box
                              {...draggableProvided.dragHandleProps}
                              sx={{
                                alignItems: 'center',
                                borderRadius: 1.5,
                                color: colors.muted,
                                cursor: 'grab',
                                display: 'flex',
                                flex: 'none',
                                justifyContent: 'center',
                                width: 26,
                                '&:hover': { bgcolor: colors.surfaceAlt, color: colors.navy },
                                '&:active': { cursor: 'grabbing' },
                              }}
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </Box>
                            <Box
                              sx={{
                                alignItems: 'center',
                                bgcolor: colors.navy,
                                borderRadius: 1.5,
                                color: '#fff',
                                display: 'flex',
                                flex: 'none',
                                fontSize: 11.5,
                                fontWeight: 800,
                                height: 24,
                                justifyContent: 'center',
                                minWidth: 24,
                                px: 0.75,
                              }}
                            >
                              {lot.number}
                            </Box>
                            <Typography
                              sx={{
                                color: colors.text,
                                flex: 1,
                                fontSize: 13,
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {lot.name}
                            </Typography>
                            <Box
                              sx={{
                                bgcolor: bg,
                                borderRadius: 20,
                                color: fg,
                                flex: 'none',
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: 0.3,
                                px: 1.1,
                                py: 0.4,
                                textTransform: 'uppercase',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {STATUS_LABEL[lot.status] || '—'}
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                    )
                  })}
                  {droppableProvided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          bgcolor: colors.surfaceAlt,
          borderTop: `1px solid ${colors.border}`,
          px: 2.5,
          py: 1.5,
        }}
      >
        <Button
          disabled={saving}
          onClick={handleClose}
          sx={{ color: colors.navy, fontSize: 13, fontWeight: 700, textTransform: 'none' }}
        >
          Cancelar
        </Button>
        <Button
          disabled={saving || lots.length === 0}
          onClick={handleAccept}
          sx={{
            bgcolor: colors.navy,
            borderRadius: 2,
            boxShadow: '0 4px 10px -4px rgba(0,75,141,.55)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            px: 2.5,
            textTransform: 'none',
            '&:hover': { bgcolor: colors.blue, boxShadow: '0 4px 10px -4px rgba(0,75,141,.55)' },
          }}
        >
          {saving ? 'Actualizando...' : 'Aceptar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
